import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Layout/AdminLayout';
import {
    CheckCircle,
    XCircle,
    Award,
    User,
    Calendar,
    Clock,
    BarChart3,
    FileText,
    Download,
    ArrowLeft
} from 'lucide-react';

const AssessmentResultView = () => {
    const { resultId } = useParams();
    const navigate = useNavigate();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchResult();
    }, [resultId]);

    const fetchResult = async () => {
        try {
            const response = await axios.get(`/api/admin/results/${resultId}`);
            setResult(response.data);
        } catch (error) {
            toast.error('Failed to load assessment result');
            navigate('/admin/results');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const response = await axios.get(`/api/admin/results/${resultId}/pdf`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `assessment-result-${result.student.name}-${result.student.email}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download PDF report');
        }
    };

    const handleDownloadCertificate = async () => {
        try {
            const response = await axios.get(`/api/admin/results/${resultId}/certificate`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `certificate-${result.student.name}-${result.student.email}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download certificate');
        }
    };

    const getPerformanceLevel = (percentage) => {
        if (percentage >= 90) return { level: 'Excellent', color: '#10B981' };
        if (percentage >= 80) return { level: 'Good', color: '#3B82F6' };
        if (percentage >= 70) return { level: 'Satisfactory', color: '#F59E0B' };
        return { level: 'Needs Improvement', color: '#EF4444' };
    };

    const getSectionPerformance = (sectionAnswers, totalQuestions) => {
        const correctAnswers = sectionAnswers.filter(answer => answer.isCorrect).length;
        const percentage = Math.round((correctAnswers / totalQuestions) * 100);
        return { correctAnswers, totalQuestions, percentage, ...getPerformanceLevel(percentage) };
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="assessment-result-view">
                    <div className="loading-container">
                        <div className="loading-spinner large" />
                        <p>Loading assessment result...</p>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    if (!result) {
        return (
            <AdminLayout>
                <div className="assessment-result-view">
                    <div className="error-container">
                        <XCircle size={48} className="error-icon" />
                        <h2>Result Not Found</h2>
                        <p>The requested assessment result could not be found.</p>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    const overall = getPerformanceLevel(result.percentage);

    // Get actual sections from assessment data
    let actualSections = [];
    let questionsData = [];

    // Try to get sections from assessment version first, then assessment
    if (result.assessmentVersion && result.assessmentVersion.sections) {
        actualSections = result.assessmentVersion.sections;
        questionsData = result.assessmentVersion.sections.reduce((all, section) => {
            return all.concat((section.questions || []).map(q => ({ ...q, sectionId: section.id })));
        }, []);
    } else if (result.assessment && result.assessment.sections) {
        actualSections = result.assessment.sections;
        if (result.assessment.questions) {
            questionsData = result.assessment.questions;
        }
    }

    // Calculate section performance based on actual sections
    const sectionPerformances = actualSections.map(section => {
        // Find questions for this section
        const sectionQuestions = questionsData.filter(q => q.section === section.id || q.sectionId === section.id);
        // Find answers for questions in this section
        const sectionAnswers = result.answers.filter(answer => {
            return sectionQuestions.some(q => q.id === answer.questionId);
        });

        const performance = getSectionPerformance(sectionAnswers, sectionQuestions.length);
        return {
            ...section,
            ...performance
        };
    });

    return (
        <AdminLayout>
            <div className="assessment-result-view">
                <div className="result-header">
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/admin/results')}
                    >
                        <ArrowLeft size={20} />
                        Back to Results
                    </button>

                    <div className="result-actions">
                        <button
                            className="btn btn-primary"
                            onClick={handleDownloadPDF}
                        >
                            <Download size={20} />
                            Download PDF Report
                        </button>
                        {result.passed && (
                            <button
                                className="btn btn-success"
                                onClick={handleDownloadCertificate}
                            >
                                <Award size={20} />
                                Download Certificate
                            </button>
                        )}
                    </div>
                </div>

                <motion.div
                    className="result-content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Student Information */}
                    <div className="result-card student-info">
                        <h2>
                            <User size={24} />
                            Student Information
                        </h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Name:</label>
                                <span>{result.student.name}</span>
                            </div>
                            <div className="info-item">
                                <label>Email:</label>
                                <span>{result.student.email}</span>
                            </div>
                            <div className="info-item">
                                <label>Completed:</label>
                                <span>
                                    <Calendar size={16} />
                                    {new Date(result.completedAt).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Assessment Code:</label>
                                <span className="assessment-code-display">
                                    {result.assessmentCode || 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Overall Performance */}
                    <div className="result-card performance-overview">
                        <h2>
                            <BarChart3 size={24} />
                            Assessment Overview
                        </h2>

                        <div className="performance-summary">
                            <div className="score-circle">
                                <div className="score-value" style={{ color: overall.color }}>
                                    {result.percentage}%
                                </div>
                                <div className="score-label">Overall Score</div>
                            </div>

                            <div className="performance-details">
                                <div className="performance-item">
                                    <span className="label">Status:</span>
                                    <span className={`status ${result.passed ? 'passed' : 'failed'}`}>
                                        {result.passed ? (
                                            <>
                                                <CheckCircle size={16} />
                                                PASSED
                                            </>
                                        ) : (
                                            <>
                                                <XCircle size={16} />
                                                FAILED
                                            </>
                                        )}
                                    </span>
                                </div>
                                <div className="performance-item">
                                    <span className="label">Performance Level:</span>
                                    <span className="level" style={{ color: overall.color }}>
                                        {overall.level}
                                    </span>
                                </div>
                                <div className="performance-item">
                                    <span className="label">Score:</span>
                                    <span>{result.score} / {result.totalPoints} points</span>
                                </div>
                                <div className="performance-item">
                                    <span className="label">Passing Score:</span>
                                    <span>70%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section Performance */}
                    <div className="result-card section-performance">
                        <h2>
                            <FileText size={24} />
                            Section Performance
                        </h2>

                        <div className="sections-grid">
                            {sectionPerformances.map(section => (
                                <div key={section.id} className="section-item">
                                    <h3>{section.title}</h3>
                                    {section.description && (
                                        <p className="section-description">{section.description}</p>
                                    )}
                                    <div className="section-stats">
                                        <div className="section-score">
                                            <span className="percentage" style={{ color: section.color }}>
                                                {section.percentage}%
                                            </span>
                                            <span className="level" style={{ color: section.color }}>
                                                {section.level}
                                            </span>
                                        </div>
                                        <div className="section-details">
                                            {section.correctAnswers} / {section.totalQuestions} correct
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Question-by-Question Results */}
                    <div className="result-card question-results">
                        <h2>
                            <CheckCircle size={24} />
                            Question-by-Question Results
                        </h2>

                        <div className="questions-grid">
                            {result.answers
                                .sort((a, b) => {
                                    const aNum = parseInt(a.questionId.replace('q', ''));
                                    const bNum = parseInt(b.questionId.replace('q', ''));
                                    return aNum - bNum;
                                })
                                .map((answer, index) => (
                                    <div key={answer.questionId} className="question-item">
                                        <div className="question-number">
                                            Q{parseInt(answer.questionId.replace('q', ''))}
                                        </div>
                                        <div className={`question-status ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                                            {answer.isCorrect ? (
                                                <CheckCircle size={16} />
                                            ) : (
                                                <XCircle size={16} />
                                            )}
                                            {answer.isCorrect ? 'Correct' : 'Incorrect'}
                                        </div>
                                        <div className="question-points">
                                            {answer.points} {answer.points === 1 ? 'point' : 'points'}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </motion.div>

                <style jsx>{`
                    .assessment-result-view {
                        padding: 24px;
                        max-width: 1200px;
                        margin: 0 auto;
                    }

                    .loading-container, .error-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        min-height: 400px;
                        text-align: center;
                    }

                    .error-icon {
                        color: var(--danger);
                        margin-bottom: 16px;
                    }

                    .result-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 32px;
                        padding-bottom: 16px;
                        border-bottom: 1px solid var(--gray-200);
                    }

                    .result-actions {
                        display: flex;
                        gap: 12px;
                    }

                    .result-content {
                        display: flex;
                        flex-direction: column;
                        gap: 24px;
                    }

                    .result-card {
                        background: var(--white);
                        border-radius: var(--border-radius-lg);
                        padding: 24px;
                        box-shadow: var(--shadow-sm);
                        border: 1px solid var(--gray-200);
                    }

                    .result-card h2 {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 20px;
                        color: var(--text-primary);
                        font-size: 20px;
                        font-weight: 600;
                    }

                    .info-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 16px;
                    }

                    .info-item {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    }

                    .info-item label {
                        font-weight: 500;
                        color: var(--text-secondary);
                        font-size: 14px;
                    }

                    .info-item span {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        color: var(--text-primary);
                        font-weight: 500;
                    }

                    .assessment-code-display {
                        font-family: 'Courier New', monospace !important;
                        font-size: 16px !important;
                        font-weight: 700 !important;
                        color: var(--primary-accent) !important;
                        background: var(--primary-bg);
                        padding: 6px 12px;
                        border-radius: 6px;
                        border: 2px solid var(--primary-accent);
                        letter-spacing: 1px;
                    }

                    .performance-summary {
                        display: flex;
                        gap: 32px;
                        align-items: center;
                    }

                    .score-circle {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        width: 120px;
                        height: 120px;
                        border-radius: 50%;
                        background: var(--gray-50);
                        border: 3px solid var(--gray-200);
                    }

                    .score-value {
                        font-size: 28px;
                        font-weight: 700;
                        line-height: 1;
                    }

                    .score-label {
                        font-size: 12px;
                        color: var(--text-secondary);
                        margin-top: 4px;
                    }

                    .performance-details {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }

                    .performance-item {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 8px 0;
                        border-bottom: 1px solid var(--gray-100);
                    }

                    .performance-item:last-child {
                        border-bottom: none;
                    }

                    .performance-item .label {
                        color: var(--text-secondary);
                        font-size: 14px;
                    }

                    .status {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-weight: 600;
                        font-size: 14px;
                    }

                    .status.passed {
                        color: var(--success);
                    }

                    .status.failed {
                        color: var(--danger);
                    }

                    .level {
                        font-weight: 600;
                    }

                    .sections-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                        gap: 20px;
                    }

                    .section-item {
                        padding: 16px;
                        border: 1px solid var(--gray-200);
                        border-radius: var(--border-radius);
                        background: var(--gray-50);
                    }

                    .section-item h3 {
                        margin-bottom: 12px;
                        color: var(--text-primary);
                        font-size: 16px;
                        font-weight: 600;
                    }

                    .section-stats {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }

                    .section-score {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    }

                    .section-score .percentage {
                        font-size: 24px;
                        font-weight: 700;
                    }

                    .section-score .level {
                        font-size: 12px;
                        font-weight: 600;
                    }

                    .section-details {
                        color: var(--text-secondary);
                        font-size: 14px;
                    }

                    .questions-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                        gap: 16px;
                    }

                    .question-item {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        padding: 16px;
                        border: 1px solid var(--gray-200);
                        border-radius: var(--border-radius);
                        background: var(--gray-50);
                        text-align: center;
                    }

                    .question-number {
                        font-weight: 600;
                        color: var(--text-primary);
                        margin-bottom: 8px;
                    }

                    .question-status {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-size: 14px;
                        font-weight: 500;
                        margin-bottom: 4px;
                    }

                    .question-status.correct {
                        color: var(--success);
                    }

                    .question-status.incorrect {
                        color: var(--danger);
                    }

                    .question-points {
                        font-size: 12px;
                        color: var(--text-secondary);
                    }

                    @media (max-width: 768px) {
                        .result-header {
                            flex-direction: column;
                            gap: 16px;
                            align-items: stretch;
                        }

                        .result-actions {
                            justify-content: center;
                        }

                        .performance-summary {
                            flex-direction: column;
                            text-align: center;
                        }

                        .sections-grid {
                            grid-template-columns: 1fr;
                        }

                        .questions-grid {
                            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
                        }
                    }
                `}</style>
            </div>
        </AdminLayout>
    );
};

export default AssessmentResultView;
